import { queryOptions } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type PublicProfile = {
  id: string;
  display_name: string | null;
  handle: string | null;
  avatar_url: string | null;
  bio: string | null;
  headline: string | null;
  website_url: string | null;
  country_code: string | null;
  niches: string[];
  languages: string[];
  socials: Record<string, string> | null;
  is_public: boolean;
  open_to_work: boolean;
  slug: string | null;
  pro_enabled: boolean;
  pro_type: string | null;
  pro_name: string | null;
  pro_logo_url: string | null;
  pro_website: string | null;
  pro_bio: string | null;
  account_mode: string;
  is_ambassador: boolean;
  ambassador_title: string | null;
};

export const PROFILE_FIELDS =
  "id,display_name,handle,slug,avatar_url,bio,headline,website_url,country_code,niches,languages,socials,is_public,open_to_work,pro_enabled,pro_type,pro_name,pro_logo_url,pro_website,pro_bio,account_mode,is_ambassador,ambassador_title";

export type SocialAccount = {
  id: string;
  user_id: string;
  platform: string;
  url: string;
  handle: string | null;
  followers: number | null;
  posts: number | null;
  engagement_rate: number | null;
  avatar_url: string | null;
  title: string | null;
  verified: boolean;
  is_public: boolean;
  source: string;
  last_synced_at: string | null;
};

export type ShowcasePost = {
  id: string;
  user_id: string;
  url: string;
  platform: string;
  title: string | null;
  caption: string | null;
  thumbnail_url: string | null;
  views: number | null;
  likes: number | null;
  sort_order: number;
};

export type CreatorService = {
  id: string;
  user_id: string;
  title: string;
  kind: string;
  description: string | null;
  price_cents: number;
  delivery_days: number;
  is_active: boolean;
};

export type ProOffer = {
  id: string;
  user_id: string;
  title: string;
  offer_type: string;
  description: string | null;
  deliverables: string[];
  budget_cents: number;
  slots: number;
  country_code: string | null;
  status: string;
  created_at: string;
};

export const SERVICE_KINDS = [
  { value: "post", label: "Feed post" },
  { value: "story", label: "Story" },
  { value: "reel", label: "Reel / short" },
  { value: "ugc", label: "UGC pack" },
  { value: "clipping", label: "Clipping" },
  { value: "sound", label: "Sound usage" },
  { value: "playlist", label: "Playlist pitch" },
  { value: "stream", label: "Stream / podcast" },
  { value: "newsletter", label: "Newsletter slot" },
  { value: "other", label: "Other" },
] as const;

export const OFFER_TYPES = [
  { value: "ugc", label: "UGC content" },
  { value: "clipping", label: "Clipping campaign" },
  { value: "sound", label: "Sound campaign" },
  { value: "playlist", label: "Playlist placement" },
  { value: "sponsorship", label: "Sponsorship" },
  { value: "ambassador", label: "Ambassador program" },
  { value: "ads", label: "Paid ads creative" },
  { value: "other", label: "Other" },
] as const;

export const PRO_TYPES = [
  { value: "brand", label: "Brand" },
  { value: "agency", label: "Agency" },
  { value: "curator", label: "Playlist curator" },
  { value: "label", label: "Record label" },
  { value: "studio", label: "Studio" },
  { value: "media", label: "Media / publisher" },
] as const;

export function formatUsdCents(cents: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

export function formatCount(value: number | null | undefined) {
  if (value == null) return "n/a";
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  return String(value);
}

/* ---------------- profile ---------------- */

export function publicProfileQuery(handle: string) {
  return queryOptions({
    queryKey: ["public-profile", handle],
    queryFn: async (): Promise<PublicProfile | null> => {
      const { data, error } = await supabase
        .from("profiles")
        .select(PROFILE_FIELDS)
        .eq("handle", handle)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as unknown as PublicProfile | null;
    },
  });
}

export function fullProfileQuery(userId: string | undefined) {
  return queryOptions({
    queryKey: ["full-profile", userId],
    enabled: Boolean(userId),
    queryFn: async (): Promise<PublicProfile | null> => {
      const { data, error } = await supabase
        .from("profiles")
        .select(PROFILE_FIELDS)
        .eq("id", userId!)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as unknown as PublicProfile | null;
    },
  });
}

/* ---------------- socials ---------------- */

export function socialAccountsQuery(userId: string | undefined) {
  return queryOptions({
    queryKey: ["social-accounts", userId],
    enabled: Boolean(userId),
    queryFn: async (): Promise<SocialAccount[]> => {
      const { data, error } = await supabase
        .from("social_accounts")
        .select("*")
        .eq("user_id", userId!)
        .order("followers", { ascending: false, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as unknown as SocialAccount[];
    },
  });
}

/* ---------------- showcase ---------------- */

export function showcasePostsQuery(userId: string | undefined) {
  return queryOptions({
    queryKey: ["showcase-posts", userId],
    enabled: Boolean(userId),
    queryFn: async (): Promise<ShowcasePost[]> => {
      const { data, error } = await supabase
        .from("showcase_posts")
        .select("*")
        .eq("user_id", userId!)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as ShowcasePost[];
    },
  });
}

/* ---------------- services ---------------- */

export function creatorServicesQuery(userId: string | undefined) {
  return queryOptions({
    queryKey: ["creator-services", userId],
    enabled: Boolean(userId),
    queryFn: async (): Promise<CreatorService[]> => {
      const { data, error } = await supabase
        .from("creator_services")
        .select("*")
        .eq("user_id", userId!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as CreatorService[];
    },
  });
}

/* ---------------- marketplace ---------------- */

export const marketplaceCreatorsQuery = queryOptions({
  queryKey: ["marketplace-creators"],
  queryFn: async () => {
    const { data, error } = await supabase
      .from("profiles")
      .select(PROFILE_FIELDS)
      .eq("is_public", true)
      .not("handle", "is", null)
      .limit(60);
    if (error) throw error;
    const profiles = (data ?? []) as unknown as PublicProfile[];
    if (!profiles.length) return [] as { profile: PublicProfile; socials: SocialAccount[] }[];

    const { data: socials } = await supabase
      .from("social_accounts")
      .select("*")
      .in(
        "user_id",
        profiles.map((p) => p.id),
      )
      .eq("is_public", true);

    const byUser = new Map<string, SocialAccount[]>();
    for (const s of ((socials ?? []) as unknown as SocialAccount[])) {
      byUser.set(s.user_id, [...(byUser.get(s.user_id) ?? []), s]);
    }
    return profiles
      .map((profile) => ({ profile, socials: byUser.get(profile.id) ?? [] }))
      .sort(
        (a, b) =>
          b.socials.reduce((n, s) => n + (s.followers ?? 0), 0) -
          a.socials.reduce((n, s) => n + (s.followers ?? 0), 0),
      );
  },
});

export const openOffersQuery = queryOptions({
  queryKey: ["open-offers"],
  queryFn: async () => {
    const { data, error } = await supabase
      .from("pro_offers")
      .select("*")
      .eq("status", "open")
      .order("created_at", { ascending: false })
      .limit(60);
    if (error) throw error;
    const offers = (data ?? []) as unknown as ProOffer[];
    if (!offers.length) return [] as { offer: ProOffer; pro: PublicProfile | null }[];
    const { data: pros } = await supabase
      .from("profiles")
      .select(PROFILE_FIELDS)
      .in("id", [...new Set(offers.map((o) => o.user_id))]);
    const byId = new Map(
      ((pros ?? []) as unknown as PublicProfile[]).map((p) => [p.id, p] as const),
    );
    return offers.map((offer) => ({ offer, pro: byId.get(offer.user_id) ?? null }));
  },
});

export function myOffersQuery(userId: string | undefined) {
  return queryOptions({
    queryKey: ["my-offers", userId],
    enabled: Boolean(userId),
    queryFn: async (): Promise<ProOffer[]> => {
      const { data, error } = await supabase
        .from("pro_offers")
        .select("*")
        .eq("user_id", userId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as ProOffer[];
    },
  });
}

export type Application = {
  id: string;
  offer_id: string;
  creator_user_id: string;
  message: string | null;
  price_cents: number;
  status: string;
  created_at: string;
  offer?: { title: string; offer_type: string } | null;
};

export function myApplicationsQuery(userId: string | undefined) {
  return queryOptions({
    queryKey: ["my-applications", userId],
    enabled: Boolean(userId),
    queryFn: async (): Promise<Application[]> => {
      const { data, error } = await supabase
        .from("offer_applications")
        .select("*,offer:pro_offers(title,offer_type)")
        .eq("creator_user_id", userId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as Application[];
    },
  });
}

export function receivedApplicationsQuery(userId: string | undefined) {
  return queryOptions({
    queryKey: ["received-applications", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const { data: offers } = await supabase
        .from("pro_offers")
        .select("id,title,offer_type")
        .eq("user_id", userId!);
      const ids = (offers ?? []).map((o) => o.id);
      if (!ids.length) return [] as (Application & { creator: PublicProfile | null })[];
      const { data, error } = await supabase
        .from("offer_applications")
        .select("*,offer:pro_offers(title,offer_type)")
        .in("offer_id", ids)
        .order("created_at", { ascending: false });
      if (error) throw error;
      const apps = (data ?? []) as unknown as Application[];
      const { data: creators } = await supabase
        .from("profiles")
        .select(PROFILE_FIELDS)
        .in("id", [...new Set(apps.map((a) => a.creator_user_id))]);
      const byId = new Map(
        ((creators ?? []) as unknown as PublicProfile[]).map((p) => [p.id, p] as const),
      );
      return apps.map((a) => ({ ...a, creator: byId.get(a.creator_user_id) ?? null }));
    },
  });
}

export type SponsorshipRequest = {
  id: string;
  creator_user_id: string;
  pro_user_id: string | null;
  brand_name: string | null;
  message: string | null;
  budget_cents: number;
  status: string;
  created_at: string;
};

export function sponsorshipRequestsQuery(userId: string | undefined) {
  return queryOptions({
    queryKey: ["sponsorship-requests", userId],
    enabled: Boolean(userId),
    queryFn: async (): Promise<SponsorshipRequest[]> => {
      const { data, error } = await supabase
        .from("sponsorship_requests")
        .select("*")
        .or(`creator_user_id.eq.${userId},pro_user_id.eq.${userId}`)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as SponsorshipRequest[];
    },
  });
}
